import {
  daysBetween,
  emojiRow,
  formatTime,
  GAME_NAME,
  GROUP_NAME,
  LAUNCH_DATE,
  type GroupDetail as GroupDetailData,
  type GroupWeek,
  londonDateString,
  TIER_COLOR,
  tierForTotal,
} from '@geosport/shared';
import { useCallback, useState, type FormEvent } from 'react';
import { Button } from '../components/Button';
import { DayNav } from '../components/DayNav';
import { Shell } from '../components/Shell';
import { Toast } from '../components/Toast';
import { api, ApiError } from '../lib/api';
import { Link, navigate, useSearch } from '../lib/router';
import { shareText } from '../lib/share';
import { useApi } from '../lib/useApi';
import { useRound } from '../store/round';

const WINDOW_DAYS = 14;

const shortDay = (d: string): string =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/** The weekly league: daily scores summed Monday to Sunday, reset every Monday. */
function WeekTable({ week }: { week: GroupWeek }) {
  return (
    <section className="mt-6" data-testid="group-week">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold">This week</h2>
        <span className="text-xs text-white/50">
          {shortDay(week.start)} – {shortDay(week.end)} · resets Monday
        </span>
      </div>
      {week.lastWinner && (
        <p className="mb-2 rounded-xl bg-accent/10 px-3 py-2 text-sm ring-1 ring-accent/30">
          🏆 Last week’s winner: <strong>{week.lastWinner.displayName}</strong> with{' '}
          {week.lastWinner.total.toLocaleString('en-GB')} points
        </p>
      )}
      {week.standings.length === 0 ? (
        <p className="text-sm text-white/50">
          Nobody has played this week yet. Every daily round counts.
        </p>
      ) : (
        <ol className="divide-y divide-white/5 rounded-2xl bg-white/5">
          {week.standings.map((e, i) => (
            <li
              key={`${e.displayName}-${i}`}
              className={`flex items-center gap-3 px-3 py-2 text-sm ${e.isMe ? 'bg-accent/10 font-semibold' : ''}`}
            >
              <span className="w-9 text-white/50 tabular-nums">{MEDAL[i + 1] ?? i + 1}</span>
              <span className="flex-1 truncate">
                {e.displayName}
                {e.isMe && <span className="ml-1 text-xs text-accent">you</span>}
              </span>
              <span className="text-xs text-white/50">
                {e.played}/7 {e.played === 1 ? 'day' : 'days'}
              </span>
              <span className="w-16 text-right font-bold tabular-nums">
                {e.total.toLocaleString('en-GB')}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
const MEDAL: Record<number, string> = { 1: '🏆', 2: '🥈', 3: '🥉' };

export function GroupDetail({ id }: { id: string }) {
  const today = londonDateString();
  const search = useSearch();
  const [date, setDate] = useState(today);
  const { data, error, loading, reload } = useApi<GroupDetailData>(`/groups/${id}?date=${date}`);
  const age = daysBetween(date, today);
  const dailyDone = useRound((s) => s.daily?.summary !== null && s.daily?.summary !== undefined);
  const [toast, setToast] = useState<string | null>(null);
  const clearToast = useCallback(() => setToast(null), []);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [showInvite, setShowInvite] = useState(search.get('new') === '1');

  const inviteUrl = data ? `${window.location.origin}/join/${data.code}` : '';

  const invite = async (): Promise<void> => {
    if (!data) return;
    const outcome = await shareText(
      `Join my ${GAME_NAME} group “${data.name}” and compare daily scores: ${inviteUrl}`,
    );
    if (outcome === 'copied') setToast('Invite link copied ✓');
    else if (outcome === 'failed') setToast('Could not share');
  };

  const rename = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      await api(`/groups/${id}/rename`, { method: 'POST', body: { name } });
      setRenaming(false);
      reload();
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Could not rename');
    } finally {
      setBusy(false);
    }
  };

  const leave = async (): Promise<void> => {
    if (!window.confirm('Leave this group?')) return;
    setBusy(true);
    try {
      await api(`/groups/${id}/leave`, { method: 'POST', body: {} });
      navigate('/groups');
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Could not leave');
      setBusy(false);
    }
  };

  const played = data?.entries.filter((e) => e.total !== null) ?? [];

  return (
    <Shell>
      <Link to="/groups" className="mb-2 inline-block text-sm text-white/60 hover:text-white">
        ‹ All groups
      </Link>
      {error && (
        <div className="rounded-2xl bg-red-500/10 p-4 text-sm text-red-200 ring-1 ring-red-500/30">
          <p className="mb-2">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="min-h-11 rounded-lg bg-white/10 px-4 font-semibold"
          >
            Retry
          </button>
        </div>
      )}
      {loading && !data && <div className="skeleton h-40 rounded-2xl" aria-hidden="true" />}
      {data && (
        <>
          <div className="mb-4 flex items-start justify-between gap-3">
            {renaming ? (
              <form onSubmit={(e) => void rename(e)} className="flex flex-1 gap-2">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  minLength={GROUP_NAME.min}
                  maxLength={GROUP_NAME.max}
                  aria-label="Group name"
                  className="min-h-11 flex-1 rounded-lg bg-white/10 px-3 text-white"
                />
                <Button type="submit" variant="secondary" disabled={busy}>
                  Save
                </Button>
              </form>
            ) : (
              <div>
                <h1 className="text-2xl font-bold" data-testid="group-name">
                  {data.name}
                </h1>
                <p className="text-sm text-white/50">
                  {data.members} {data.members === 1 ? 'member' : 'members'} · code{' '}
                  <span className="font-mono">{data.code}</span>
                </p>
              </div>
            )}
            {data.isOwner && !renaming && (
              <button
                type="button"
                onClick={() => {
                  setName(data.name);
                  setRenaming(true);
                }}
                className="min-h-10 rounded-lg px-3 text-sm text-white/70 hover:bg-white/10"
              >
                Rename
              </button>
            )}
          </div>

          {showInvite && (
            <div className="mb-4 rounded-2xl bg-accent/10 p-4 ring-1 ring-accent/30">
              <div className="font-bold">Invite your friends</div>
              <p className="text-sm text-white/70">
                Send them this link, or the code <span className="font-mono">{data.code}</span>.
              </p>
              <p className="mt-1 truncate font-mono text-xs text-white/60">{inviteUrl}</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={() => void invite()}>
                  Share invite
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowInvite(false)}>
                  Done
                </Button>
              </div>
            </div>
          )}

          <DayNav
            date={date}
            onChange={setDate}
            canBack={age < WINDOW_DAYS && date > LAUNCH_DATE}
            canForward={age > 0}
            subtitle={`Puzzle #${data.puzzleNo} · ${played.length} of ${data.members} played`}
          />

          <ol className="divide-y divide-white/5 rounded-2xl bg-white/5" data-testid="group-board">
            {data.entries.map((e, i) => {
              const rank = e.total === null ? null : i + 1;
              return (
                <li
                  key={`${e.displayName}-${i}`}
                  className={`flex items-center gap-3 px-3 py-2 text-sm ${e.isMe ? 'bg-accent/10 font-semibold' : ''}`}
                >
                  <span className="w-9 text-white/50 tabular-nums">
                    {rank === null ? '—' : (MEDAL[rank] ?? rank)}
                  </span>
                  <div className="flex-1 truncate">
                    {e.displayName}
                    {e.isOwner && <span className="ml-1 text-xs text-white/40">owner</span>}
                    {e.isMe && <span className="ml-1 text-xs text-accent">you</span>}
                    {e.bases && (
                      <div className="text-xs tracking-wide text-white/70">{emojiRow(e.bases)}</div>
                    )}
                  </div>
                  {e.total === null ? (
                    <span className="text-xs text-white/40">Not played yet</span>
                  ) : (
                    <>
                      <span
                        className="font-bold tabular-nums"
                        style={{ color: TIER_COLOR[tierForTotal(e.total)] }}
                      >
                        {e.total}
                      </span>
                      <span className="w-12 text-right text-xs text-white/50 tabular-nums">
                        {formatTime(e.timeMs ?? 0)}
                      </span>
                    </>
                  )}
                </li>
              );
            })}
          </ol>

          {age === 0 && !dailyDone && (
            <Button className="mt-4 w-full" onClick={() => navigate('/')}>
              Play today’s round →
            </Button>
          )}

          {data.week && <WeekTable week={data.week} />}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowInvite(true)}>
              Invite
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-200"
              onClick={() => void leave()}
              disabled={busy}
            >
              Leave group
            </Button>
          </div>
        </>
      )}
      <Toast message={toast} onDone={clearToast} />
    </Shell>
  );
}
